import React, { useState, useEffect } from 'react';
import AnimatedSection from '../common/AnimatedSection';

const TechnologySection = () => {
    // Add window width tracking for responsive design
    const [windowWidth, setWindowWidth] = useState(
        typeof window !== "undefined" ? window.innerWidth : 1200
    );

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // Define breakpoints
    const isMobile = windowWidth <= 576;
    const isTablet = windowWidth <= 820 && windowWidth > 576;
    const isDesktop = windowWidth > 820;

    // Dynamic responsive styles
    const responsiveStyles = {
        section: {
            padding: isMobile ? '50px 0' : isTablet ? '70px 0' : '90px 0',
        },
        sectionTitle: {
            fontSize: isMobile ? '1.8rem' : isTablet ? '2.2rem' : '2.5rem',
        },
        featureTitle: {
            fontSize: isMobile ? '1.5rem' : isTablet ? '1.6rem' : '1.8rem',
        },
        imageFrame: {
            height: isMobile ? '250px' : '350px',
            maxWidth: isMobile ? '100%' : '500px',
        },
        twoColumnLayout: {
            flexDirection: isMobile ? 'column-reverse' : 'row',
            marginTop: isMobile ? '40px' : isTablet ? '60px' : '80px',
            gap: isMobile ? '30px' : '40px',
        },
        column: {
            flex: isMobile ? '1 1 100%' : '1 1 450px',
        },
        columnWithImage: {
            flex: isMobile ? '1 1 100%' : '1 1 450px',
            marginBottom: isMobile ? '20px' : 0,
        },
        marketsGrid: {
            gridTemplateColumns: isMobile 
                ? 'repeat(1, 1fr)' 
                : isTablet 
                    ? 'repeat(2, 1fr)' 
                    : 'repeat(3, 1fr)',
        },
        text: {
            fontSize: isMobile ? '1rem' : '1.1rem',
        },
        featureItem: {
            fontSize: isMobile ? '1rem' : '1.1rem',
        },
        sectionDivider: {
            marginBottom: isMobile ? '30px' : '50px',
            width: isMobile ? '60px' : '80px',
        }
    };

    return (
        <section style={{...styles.section, ...responsiveStyles.section}}>
            <div style={styles.container}>
                <AnimatedSection animation="fadeInUp">
                    <h2 style={{...styles.sectionTitle, ...responsiveStyles.sectionTitle}}>
                        Technology & <span style={styles.highlight}>Innovation</span>
                    </h2>
                    <div style={{...styles.sectionDivider, ...responsiveStyles.sectionDivider}}></div>
                    
                    <div style={{...styles.twoColumnLayout, ...responsiveStyles.twoColumnLayout, marginTop: 0}}>
                        <div style={{...styles.columnWithImage, ...responsiveStyles.columnWithImage}}>
                            <div style={{...styles.imageFrame, ...responsiveStyles.imageFrame}}>
                                <img 
                                    src="https://res.cloudinary.com/dknsjvavr/image/upload/v1753202851/Advanced-Elevator-Technology_gnvj41.png" 
                                    alt="Advanced Elevator Technology" 
                                    style={styles.sectionImage}
                                />
                            </div>
                        </div>
                        
                        <div style={{...styles.column, ...responsiveStyles.column}}>
                            <h3 style={{...styles.featureTitle, ...responsiveStyles.featureTitle}}>Cutting-Edge Solutions</h3>
                            <p style={{...styles.text, ...responsiveStyles.text}}>
                                Dee Cee Elevators leverages the latest technology, offering IoT-enabled, energy-efficient 
                                elevators that enhance operational efficiency and sustainability. Our innovations include:
                            </p>
                            
                            <ul style={styles.featureList}>
                                {[
                                    'Smart elevators with touchless operation',
                                    'AI-driven predictive maintenance',
                                    'Energy-regenerative drive systems',
                                    'Biometric access controls for enhanced security',
                                    'Custom design solutions for architectural integration'
                                ].map((item, index) => (
                                    <li key={index} style={{...styles.featureItem, ...responsiveStyles.featureItem}}>
                                        <i className="fas fa-check-circle" style={styles.featureIcon}></i>
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div style={{...styles.twoColumnLayout, ...responsiveStyles.twoColumnLayout}}>
                        <div style={{...styles.column, ...responsiveStyles.column}}>
                            <h3 style={{...styles.featureTitle, ...responsiveStyles.featureTitle}}>Markets We Serve</h3>
                            <p style={{...styles.text, ...responsiveStyles.text}}>
                                We provide specialized elevator solutions across diverse markets, each with 
                                tailored features to meet specific requirements:
                            </p>
                            
                            <div style={{...styles.marketsGrid, ...responsiveStyles.marketsGrid}}>
                                {[
                                    { icon: 'fas fa-home', text: 'Residential' },
                                    { icon: 'fas fa-building', text: 'Commercial' },
                                    { icon: 'fas fa-industry', text: 'Industrial' },
                                    { icon: 'fas fa-hospital', text: 'Healthcare' },
                                    { icon: 'fas fa-hotel', text: 'Hospitality' },
                                    { icon: 'fas fa-shopping-bag', text: 'Retail' }
                                ].map((market, index) => (
                                    <div key={index} style={styles.marketItem}>
                                        <i className={market.icon} style={styles.marketIcon}></i>
                                        <span style={styles.marketText}>{market.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                        
                        <div style={{...styles.columnWithImage, ...responsiveStyles.columnWithImage}}>
                            <div style={{
                                ...styles.imageFrame, 
                                ...responsiveStyles.imageFrame, 
                                marginLeft: isMobile ? 0 : 'auto'
                            }}>
                                <img 
                                    src="https://res.cloudinary.com/dknsjvavr/image/upload/v1753824806/ChatGPT_Image_Jul_30_2025_03_02_34_AM_v3nqbg.png" 
                                    alt="Markets Served" 
                                    style={styles.sectionImage}
                                />
                            </div>
                        </div>
                    </div>
                </AnimatedSection>
            </div>
        </section>
    );
};

const styles = {
    section: {
        backgroundColor: '#ffffff',
    },
    container: {
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '0 20px',
    },
    sectionTitle: {
        fontWeight: '800',
        textAlign: 'center',
        marginBottom: '15px',
        color: '#2c3e50',
    },
    highlight: {
        color: '#163ba2',
    },
    sectionDivider: {
        height: '4px',
        backgroundColor: '#163ba2',
        margin: '0 auto',
        borderRadius: '2px',
    },
    text: {
        color: '#4b5563',
        lineHeight: '1.8',
        marginBottom: '20px',
    },
    twoColumnLayout: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },
    column: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    columnWithImage: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imageFrame: {
        width: '100%',
        overflow: 'hidden',
        borderRadius: '10px',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
    },
    sectionImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        transition: 'transform 0.5s ease',
        ':hover': {
            transform: 'scale(1.05)',
        }
    },
    featureTitle: {
        fontWeight: '700',
        marginBottom: '20px',
        color: '#2c3e50',
    },
    featureList: {
        listStyle: 'none',
        padding: 0,
        margin: '30px 0',
    },
    featureItem: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '15px',
        color: '#4b5563',
    },
    featureIcon: {
        color: '#163ba2',
        marginRight: '12px',
        fontSize: '1.2rem',
    },
    marketsGrid: {
        display: 'grid',
        gap: '15px',
        marginTop: '25px',
    },
    marketItem: {
        display: 'flex',
        alignItems: 'center',
        backgroundColor: '#f8fafc',
        padding: '15px',
        borderRadius: '8px',
        border: '1px solid #e5e7eb',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        cursor: 'pointer',
        ':hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.05)',
        }
    },
    marketIcon: {
        color: '#163ba2',
        fontSize: '1.2rem',
        marginRight: '10px',
    },
    marketText: {
        color: '#4b5563',
        fontWeight: '500',
    },
};

export default TechnologySection;